import { useTranslation } from 'react-i18next';
import { useBasePath } from '~/common/hooks/useBasePath';
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useForm } from '@conform-to/react';
import { CustomSelectItem, Select, SelectContent, SelectTrigger, SelectValue } from '~/common/components/ui/select';
import { cn } from '~/common/utils/styleUtil';
import { Button } from '~/common/components/ui/button';
import { FORM_FIELDS } from '~/common/constants/formfields';
import { DialogFooter, DialogClose } from '~/common/components/ui/dialog';
import type { ITemplateLookupDtoData } from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import ProgressButton from '~/common/components/ProgressButton';
import { toast } from 'sonner';
import { clientFetch } from '~/common/utils/clientFetch.client';
import { useSendAddToListToGoogleAnalytics } from '~/tracking/googleAnalytics';
import type { AddArticleToTemplateInput } from '~/features/templates/routes/resources/AddArticleToTemplateRoute';

export function TemplateSelect({
  onClose,
  articles,
  templates,
  isLoading,
  isAgh,
  preselectedTemplateId
}: Readonly<{
  onClose: () => void;
  articles: AddArticleToTemplateInput[];
  isLoading: boolean;
  templates: ITemplateLookupDtoData[];
  isAgh: boolean;
  preselectedTemplateId?: number;
}>) {
  const { t } = useTranslation();
  const { path } = useBasePath();
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [inProgress, setInProgress] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const sendAddToListToGoogleAnalytics = useSendAddToListToGoogleAnalytics();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [form, fields] = useForm({
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput'
  });

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setInProgress(true);
        const formData = new FormData(event.target);
        formData.append(FORM_FIELDS.Intent, FORM_FIELDS.Select);

        const response = await clientFetch(`${path}/resources/templates/addarticletotemplate`, {
          method: 'POST',
          body: formData
        });

        const data = (await response.json()) as { ok: boolean; message: string };
        if (data.message) {
          if (data.ok) {
            sendAddToListToGoogleAnalytics(articles);
            toast.success(data.message);
            onClose();
          } else {
            toast.error(data.message);
          }
        }
      } catch {
        toast.error(t('Ein technischer Fehler ist aufgetreten. Bitte laden Sie den Webshop neu!'));
      } finally {
        setInProgress(false);
      }
    },
    [articles, onClose, path, sendAddToListToGoogleAnalytics, t]
  );

  useEffect(() => {
    if (!selectedTemplateId && templates?.length > 0) {
      if (preselectedTemplateId) {
        if (templates.some(template => template.id === preselectedTemplateId && !template.isArticleAlreadyInTemplate)) {
          // Set the preselected template as the selected template if it is available and not already selected
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setSelectedTemplateId(preselectedTemplateId);
        }
      } else {
        const firstAvailableTemaplate = templates.find(inv => !inv.isArticleAlreadyInTemplate);
        if (firstAvailableTemaplate) {
          // Set the first available template as the selected template if no preselected template is available and no template is already selected
          setSelectedTemplateId(firstAvailableTemaplate.id);
        }
      }
    }
  }, [selectedTemplateId, templates, preselectedTemplateId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Element;

      // Check if the click is inside our form component
      if (formRef.current?.contains(target)) {
        return;
      }

      // Check if the click is inside the Select portal element
      const isInSelectPortal =
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-radix-select-content]') ||
        target.closest('[data-radix-select-item]') ||
        target.closest('[data-radix-select-trigger]');

      // Only close if click is outside both our component and the select portal
      if (!isInSelectPortal && isOpen) {
        // Delay closing the select to allow to work properly when clicking on the select trigger with touch devices
        setTimeout(() => setIsOpen(false), 300);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  const selectedTemplate = useMemo(
    () => templates.find(template => template.id === selectedTemplateId),
    [templates, selectedTemplateId]
  );
  const isAlreadySelected = useMemo(
    () => selectedTemplateId && selectedTemplate?.isArticleAlreadyInTemplate,
    [selectedTemplateId, selectedTemplate]
  );

  const disableAddButton =
    selectedTemplateId == null ||
    isAlreadySelected ||
    (templates?.every(template => template.isArticleAlreadyInTemplate) ?? false);

  const preselectedTemplate = useMemo(
    () => templates.find(template => template.id === preselectedTemplateId),
    [templates, preselectedTemplateId]
  );

  const isPreselectedTemplateAlreadySelected = useMemo(
    () => preselectedTemplate?.isArticleAlreadyInTemplate,
    [preselectedTemplate]
  );

  return (
    <form
      ref={formRef}
      method="post"
      className={cn('flex flex-col h-full desktop:justify-between desktop:h-full')}
      onSubmit={handleSubmit}
    >
      <input name="articles" type="hidden" defaultValue={JSON.stringify(articles)} />
      <input name="selectedTemplateId" type="hidden" defaultValue={selectedTemplateId?.toString()} />
      <div className={'flex-1'}>
        <p className={'my-2'}>
          {t(
            isAgh
              ? 'In welche Einkaufsliste möchten Sie den Artikel hinzufügen?'
              : 'In welche Bestellvorlage möchten Sie den Artikel hinzufügen?'
          )}
        </p>
        <div>
          <Select
            disabled={isLoading}
            open={isOpen}
            onOpenChange={setIsOpen}
            value={selectedTemplateId?.toString()}
            onValueChange={value => {
              setSelectedTemplateId(Number.parseInt(value, 10));
            }}
          >
            <SelectTrigger
              className={cn('w-full', !isLoading && !selectedTemplateId && 'border-primary focus:ring-primary')}
              data-testid={`template-select`}
            >
              {!selectedTemplateId && isPreselectedTemplateAlreadySelected ? (
                <div className={'flex justify-between content-between w-full opacity-60'}>
                  <div>{preselectedTemplate?.name}</div>
                  <div className={'text-primary pr-2'}>
                    {t(isAgh ? 'in Einkaufsliste vorhanden' : 'in Vorlage vorhanden')}
                  </div>
                </div>
              ) : (
                <SelectValue />
              )}
            </SelectTrigger>
            <SelectContent>
              {templates.map(template => (
                <CustomSelectItem
                  key={template.id}
                  value={template.id.toString()}
                  data-testid={`template-select-${template.id}`}
                  badge={
                    template.isArticleAlreadyInTemplate ? (
                      <div className={'text-primary'}>
                        {t(isAgh ? 'in Einkaufsliste vorhanden' : 'in Vorlage vorhanden')}
                      </div>
                    ) : null
                  }
                  disabled={template.isArticleAlreadyInTemplate}
                >
                  {template.name}
                </CustomSelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-1 min-h-6 text-sm text-primary">{fields.name.errors?.map(e => t(e as any))}</div>
      </div>
      <DialogFooter className={'flex flex-col desktop:flex-row gap-2 desktop:gap-0'}>
        <DialogClose asChild>
          <Button variant="primaryOutline" data-testid={'template-select-close'}>
            {t('Abbrechen')}
          </Button>
        </DialogClose>
        <ProgressButton
          inProgress={inProgress}
          name={FORM_FIELDS.Intent}
          value={FORM_FIELDS.Select}
          type="submit"
          disabled={disableAddButton}
        >
          {t('Hinzufügen')}
        </ProgressButton>
      </DialogFooter>
    </form>
  );
}
